// ============================================================
// EditBook.jsx
// ============================================================
import React, { useEffect, useState, useRef, useLayoutEffect, useMemo, useCallback } from 'react';
import { Flex, Box, Text } from 'rebass';
import store from 'store';
import Grid from 'app/grid/index';
import PageSlider from './components/PageSlider';
import SliderPageRenderer from './components/SliderComponents/SliderPageRenderer';
import { useWindowSize } from 'react-use';
import { usePageContext } from './PageContext';
import Ftue, { FTUE_STORAGE_KEY } from './Ftue';

function calculateZoomValue(width) {
    const baseZoom = 0.044;
    const baseWidth = 1512;
    return (baseZoom * (width / baseWidth)) * 100;
}

export const PageComponent = React.memo(function PageComponent({
    setCurrentPage,
    totalPagesData,
    setBoxWidth,
    currentPage,
    boxWidth,
}) {
    const { width } = useWindowSize();
    const [boxHeight, setHeightBox] = useState(null);

    const pageRefs = useRef([]);
    const measureRefs = useRef([]);
    // Track whether we've set the height yet
    const heightSetRef = useRef(false);

    // ✅ Memoized zoom — only recomputes when screen width changes
    const zoomValue = useMemo(() => calculateZoomValue(width), [width]);

    // ✅ useLayoutEffect uses memoized zoomValue, not a function call
    useLayoutEffect(() => {
        if (!measureRefs.current.length) return;
        const widths = measureRefs.current.map(
            (el) => el?.getBoundingClientRect().width || 0
        );
        const maxWidth = Math.max(...widths);
        setBoxWidth(maxWidth * 2 + 30);
    }, [totalPagesData, zoomValue, setBoxWidth]);

    // ✅ Stable ref: used in Flex ref callback to measure height only once
    const handleFlexRef = useCallback((el) => {
        if (!el || heightSetRef.current) return;
        const h = el.getBoundingClientRect().height;
        if (h > 0) {
            heightSetRef.current = true;
            setHeightBox(h);
        }
    }, []);

    // ✅ useCallback — stable reference across renders
    const scrollToIndex = useCallback((index) => {
        const page = pageRefs.current[index];
        if (page) {
            page.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        }
    }, []);

    useEffect(() => {
        if (!currentPage) return;
        const index = totalPagesData.findIndex(
            (pair) => pair.some((p) => p.id === currentPage?.id)
        );

        if (index > -1) scrollToIndex(index);
    }, [currentPage, scrollToIndex]);

    return (
        <Grid
            gridTemplateColumns={`repeat(1, ${boxWidth / 2 - 10}px)`}
            gridGap="10px"
            width="100%"
        >
            {totalPagesData.map((pair, index) => {

                const isSelected = pair.some((p) => p.id === currentPage?.id);
                const startPageNum = (index * 2);
                const endPageNum = startPageNum + 1;
                return (
                    <Box
                        key={index}
                        ref={(el) => { pageRefs.current[index] = el; }}
                        mb="14px"
                    >
                        <Box
                            sx={{
                                transform: `scale(${zoomValue / 100})`,
                                transformOrigin: 'top left',
                                width: 'max-content',
                                height: boxHeight ? `${boxHeight}px` : 'auto',

                            }}
                            ref={(el) => { measureRefs.current[index] = el; }}
                        >
                            <Text fontSize="200px" textAlign="center" fontWeight="bold" color="#888" mb="50px">
                                Page {startPageNum}-{endPageNum}
                            </Text>
                            <Flex sx={{
                                gap: '3.121px',
                                border:
                                    isSelected
                                        ? '33px solid #C470E6'
                                        : undefined,
                            }} ref={handleFlexRef}>
                                {pair.map((page, idx) => (<Box key={page.id || idx}>
                                    <SliderPageRenderer
                                        index={idx}
                                        {...page}
                                        zoom={100}
                                        isSelected={currentPage?.id === page?.id}
                                        attributes={page.attributes || page}
                                        relationships={
                                            page.relationships || {
                                                layers: { data: page.layers },
                                            }
                                        }
                                        setCurrentPage={setCurrentPage}
                                    />
                                </Box>
                                ))}
                            </Flex>
                        </Box>
                    </Box>
                )
            })}
        </Grid>
    );
});

const EditBook = ({
    firstBookId,
    revalidate,
    HeaderComponent,
}) => {
    const [boxWidth, setBoxWidth] = useState(null);
    const {
        currentPage,
        setCurrentPage,
        selectedPageIndex,
        setSelectedPageIndex,
        totalPagesData,
        setTotalPagesData,
        loading,
        layerEditor,
    } = usePageContext();
    const [showFtue, setShowFtue] = useState(() => !store.get(FTUE_STORAGE_KEY));
    const dismissFtue = () => {
        store.set(FTUE_STORAGE_KEY, true);
        setShowFtue(false);
    };

    const emptyPage = useMemo(() => {
        const firstPage = totalPagesData?.[0];
        const width = firstPage?.attributes?.width ?? firstPage?.width;
        const height = firstPage?.attributes?.height ?? firstPage?.height;
        return {
            id: '', isEmpty: true, attributes: {
                width, height, 'background-colour': "#ffffff",
                enabled: true
            }, width, height
        };
    }, [totalPagesData?.[0]]);

    const sortedTotalPagesData = useMemo(() => {
        if (!totalPagesData || totalPagesData.length === 0) return [];
        return [emptyPage, ...totalPagesData].reduce((result, item, index, array) => {
            if (index % 2 === 0) {
                result.push(array.slice(index, index + 2));
            }
            return result;
        }, []);
    }, [totalPagesData, emptyPage]);

    const totalPagesDataForSlider = useMemo(
        () => (totalPagesData ? [emptyPage, ...totalPagesData] : []),
        [totalPagesData, emptyPage]
    );

    // PageSlider receives totalPagesDataForSlider (emptyPage at index 0, real pages
    // shifted by +1).  selectedPageIndex from PageContext is 0-based in the original
    // data, so we add 1 when passing down and subtract 1 coming back up.
    const sliderSelectedPageIndex = selectedPageIndex + 1;

    const handleSliderSetSelectedPageIndex = useCallback((idx) => {
        setSelectedPageIndex(Math.max(0, idx - 1));
    }, [setSelectedPageIndex]);

    const handleSliderSetCurrentPage = useCallback((page) => {
        if (!page || page.isEmpty) return;
        setCurrentPage(page);
    }, [setCurrentPage]);

    if (!totalPagesData || totalPagesData.length === 0) return null;

    return (
        <Flex width="100%">
            <Box
                sx={{
                    height: 'calc(100vh - 130px)',
                    position: 'fixed',
                    overflowY: 'scroll',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#C9C3D8 transparent',
                    '&::-webkit-scrollbar': { width: '8px' },
                    '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#C9C3D8',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#b4adc8' },
                }}
            >
                <PageComponent
                    setCurrentPage={setCurrentPage}
                    firstBookId={firstBookId}
                    totalPagesData={sortedTotalPagesData}
                    currentPage={currentPage}
                    revalidate={revalidate}
                    boxWidth={boxWidth}
                    setBoxWidth={setBoxWidth}
                />
            </Box>
            <PageSlider
                currentPage={currentPage}
                setCurrentPage={handleSliderSetCurrentPage}
                selectedPageIndex={sliderSelectedPageIndex}
                setSelectedPageIndex={handleSliderSetSelectedPageIndex}
                totalPagesData={totalPagesDataForSlider}
                setTotalPagesData={setTotalPagesData}
                loading={loading}
                boxWidth={boxWidth}
                HeaderComponent={HeaderComponent}
                firstBookId={firstBookId}
                layerEditor={layerEditor} />
            {showFtue && (
                <Ftue onClose={dismissFtue} />
            )}
        </Flex>
    );
};

export default EditBook;
